import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Fade, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export function formatAutosaveTimestamp(iso) {
    if (!iso) return "earlier";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return "earlier";
    }
}

export function readAutosaveDraft(storageKey) {
    if (!storageKey) return null;
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.data) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function removeAutosaveDraft(storageKey) {
    if (storageKey) {
        try {
            localStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
    }
}

/**
 * Debounced localStorage autosave with restore/discard prompt on mount.
 */
export default function useLocalStorageAutosave({
    storageKey,
    state,
    enabled = true,
    debounceMs = 800,
}) {
    const [draftPrompt, setDraftPrompt] = useState(null);
    const [showSavedIndicator, setShowSavedIndicator] = useState(false);
    const draftResolvedRef = useRef(false);
    const skipSaveRef = useRef(true);
    const lastKeyRef = useRef(null);

    useEffect(() => {
        if (!enabled || !storageKey) {
            setDraftPrompt(null);
            draftResolvedRef.current = true;
            return;
        }

        if (lastKeyRef.current !== storageKey) {
            lastKeyRef.current = storageKey;
            skipSaveRef.current = true;
            const draft = readAutosaveDraft(storageKey);
            if (draft) {
                setDraftPrompt(draft);
                draftResolvedRef.current = false;
            } else {
                setDraftPrompt(null);
                draftResolvedRef.current = true;
            }
        }
    }, [storageKey, enabled]);

    useEffect(() => {
        if (!enabled || !storageKey || !draftResolvedRef.current || draftPrompt) {
            return undefined;
        }

        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return undefined;
        }

        const timer = setTimeout(() => {
            try {
                localStorage.setItem(
                    storageKey,
                    JSON.stringify({
                        savedAt: new Date().toISOString(),
                        data: state,
                    })
                );
                setShowSavedIndicator(true);
            } catch {
                /* quota or private mode */
            }
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [storageKey, state, enabled, debounceMs, draftPrompt]);

    useEffect(() => {
        if (!showSavedIndicator) return undefined;
        const timer = setTimeout(() => setShowSavedIndicator(false), 2000);
        return () => clearTimeout(timer);
    }, [showSavedIndicator]);

    const restoreDraft = useCallback(() => {
        if (!draftPrompt?.data) return null;
        const data = draftPrompt.data;
        skipSaveRef.current = true;
        draftResolvedRef.current = true;
        setDraftPrompt(null);
        return data;
    }, [draftPrompt]);

    const discardDraft = useCallback(() => {
        removeAutosaveDraft(storageKey);
        skipSaveRef.current = true;
        draftResolvedRef.current = true;
        setDraftPrompt(null);
    }, [storageKey]);

    const clearDraft = useCallback(() => {
        removeAutosaveDraft(storageKey);
        skipSaveRef.current = true;
        setDraftPrompt(null);
    }, [storageKey]);

    return {
        draftPrompt,
        restoreDraft,
        discardDraft,
        clearDraft,
        showSavedIndicator,
    };
}

export function AutosaveDraftAlert({ draft, onRestore, onDiscard }) {
    if (!draft) return null;

    return (
        <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: 2 }}
            action={
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button color="inherit" size="small" variant="outlined" onClick={onRestore}>
                        Restore draft
                    </Button>
                    <Button color="inherit" size="small" onClick={onDiscard}>
                        Discard
                    </Button>
                </Stack>
            }
        >
            You have an unsaved draft from {formatAutosaveTimestamp(draft.savedAt)}. Restore it?
        </Alert>
    );
}

export function AutosaveSavedIndicator({ show }) {
    return (
        <Fade in={show} unmountOnExit>
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: "text.secondary",
                }}
            >
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight={500}>
                    Draft saved
                </Typography>
            </Stack>
        </Fade>
    );
}
